import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { preventasApi, cajaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSucursal } from '../context/SucursalContext';
import { formatDate, formatDateTime } from '../utils/dateHelpers';
import PagoMixtoForm, { lineaVacia, validarPagoMixto } from '../components/PagoMixtoForm';

const PreventaDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sucursalActual } = useSucursal();

  const [preventa, setPreventa] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showPagoModal, setShowPagoModal] = useState(false);
  const [pagando, setPagando] = useState(false);
  const [pagoForm, setPagoForm] = useState({
    monto_pago: '',
    metodo_pago: 'Efectivo',
    referencia_pago: '',
    cuenta_bancaria_id: '',
    pos_id: '',
    pago_mixto: [lineaVacia()]
  });
  const [cuentas, setCuentas] = useState([]);
  const [posTerminales, setPosTerminales] = useState([]);

  const loadPreventa = async () => {
    try {
      const data = await preventasApi.getById(id);
      setPreventa(data);
    } catch (err) {
      console.error('Error:', err);
      alert('Preventa no encontrada');
      navigate('/preventas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreventa();
  }, [id]);

  useEffect(() => {
    const tiendaId = user?.tienda_id;
    if (!tiendaId) return;
    Promise.all([cajaApi.getCuentas(tiendaId), cajaApi.getPos(tiendaId)])
      .then(([resCuentas, resPos]) => {
        setCuentas(resCuentas || []);
        setPosTerminales(resPos || []);
      })
      .catch(err => console.error('Error cargando cuentas/POS:', err));
  }, [user]);

  const formatMoney = (amount) => `Q${parseFloat(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${date.getDate()} de ${meses[date.getMonth()]} de ${date.getFullYear()}`;
  };

  const abrirModalPago = () => {
    setPagoForm({
      monto_pago: preventa.saldo,
      metodo_pago: 'Efectivo',
      referencia_pago: '',
      cuenta_bancaria_id: '',
      pos_id: '',
      pago_mixto: [lineaVacia()]
    });
    setShowPagoModal(true);
  };

  const handleCompletarPago = async () => {
    const monto = parseFloat(pagoForm.monto_pago) || 0;
    if (monto <= 0) {
      alert('El monto a pagar debe ser mayor a 0');
      return;
    }
    if (pagoForm.metodo_pago === 'Transferencia' && !pagoForm.cuenta_bancaria_id) {
      alert('Debe seleccionar la cuenta bancaria');
      return;
    }
    if (pagoForm.metodo_pago === 'POS' && !pagoForm.pos_id) {
      alert('Debe seleccionar la terminal POS');
      return;
    }
    if (['Transferencia', 'Cheque'].includes(pagoForm.metodo_pago) && !pagoForm.referencia_pago.trim()) {
      alert('Debe ingresar el número de referencia');
      return;
    }
    if (pagoForm.metodo_pago === 'Mixto') {
      const errorMixto = validarPagoMixto(pagoForm.pago_mixto, monto);
      if (errorMixto) {
        alert(errorMixto);
        return;
      }
    }

    setPagando(true);
    try {
      await preventasApi.completarPago(preventa.id, {
        monto_pago: monto,
        metodo_pago: pagoForm.metodo_pago,
        referencia_pago: pagoForm.referencia_pago,
        cuenta_bancaria_id: pagoForm.cuenta_bancaria_id || null,
        pos_id: pagoForm.pos_id || null,
        pago_mixto: pagoForm.metodo_pago === 'Mixto' ? pagoForm.pago_mixto : undefined,
        usuario_id: user?.id
      });
      setShowPagoModal(false);
      loadPreventa();
      alert('Pago registrado exitosamente');
    } catch (err) {
      alert('Error al registrar el pago: ' + (err.response?.data?.error || err.message));
    } finally {
      setPagando(false);
    }
  };

  const handlePrint = () => {
    if (!preventa) return;

    const printWindow = window.open('about:blank', '_blank', 'width=400,height=600');
    if (!printWindow) {
      alert('Por favor habilite las ventanas emergentes para imprimir');
      return;
    }

    const tiendaNombre = sucursalActual?.nombre || preventa.nombre_tienda || 'INTERSECOM';
    const tiendaDireccion = sucursalActual?.direccion || '15 Av. 1-340 Zona 5, San Marcos';
    const tiendaTelefono = sucursalActual?.telefono || '7760-3991 / 7725-4830';
    const items = preventa.items || [];

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Preventa N° ${preventa.numero_preventa}</title>
          <style>
            @media print {
              @page { size: 72mm auto; margin: 0 !important; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              body {
                font-family: Arial, Helvetica, sans-serif !important;
                font-size: 11px !important; font-weight: 600 !important; line-height: 1.3 !important;
                margin: 0 !important; padding: 0 !important; width: 72mm !important;
                color: #000 !important; background: #fff !important;
              }
              .rc { width: 72mm !important; padding: 2mm !important; box-sizing: border-box !important; }
              .rc-header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 3mm; margin-bottom: 3mm; }
              .rc-logo { font-size: 16px !important; font-weight: 900 !important; letter-spacing: 1px; }
              .rc-sub { font-size: 10px !important; font-weight: 700 !important; margin-bottom: 2px; }
              .rc-tipo { text-align: center; font-size: 13px !important; font-weight: 900 !important; margin: 3mm 0; padding: 2mm; border: 2px solid #000; }
              .rc-num { text-align: center; font-size: 12px !important; font-weight: 800 !important; margin-bottom: 3mm; }
              .rc-row { display: flex; justify-content: space-between; font-size: 10px !important; font-weight: 600 !important; margin-bottom: 1mm; }
              .rc-label { font-weight: 800 !important; }
              .rc-table { width: 100%; border-collapse: collapse; margin: 3mm 0; }
              .rc-table th { border-bottom: 2px solid #000; padding: 2mm 1mm; font-weight: 900 !important; font-size: 9px !important; }
              .rc-table td { padding: 2mm 1mm; border-bottom: 1px dashed #000; font-size: 10px !important; }
              .rc-table .cc { width: 12%; text-align: center; }
              .rc-table .cd { width: 48%; }
              .rc-table .cp { width: 18%; text-align: right; }
              .rc-table .ct { width: 22%; text-align: right; font-weight: 700 !important; }
              .rc-total-s { border-top: 2px solid #000; padding-top: 3mm; margin-top: 2mm; }
              .rc-total { display: flex; justify-content: space-between; font-size: 13px !important; font-weight: 900 !important; padding: 2mm; margin-top: 2mm; }
              .rc-saldo { display: flex; justify-content: space-between; font-size: 14px !important; font-weight: 900 !important; padding: 2mm; border: 2px solid #000; margin-top: 2mm; }
              .rc-firmas { display: flex; justify-content: space-between; margin-top: 10mm; }
              .rc-firma { text-align: center; width: 45%; }
              .rc-firma-l { border-top: 1px solid #000; margin-bottom: 2mm; margin-top: 8mm; }
              .rc-firma-t { font-size: 9px !important; font-weight: 700 !important; }
              .rc-footer { text-align: center; font-size: 9px !important; margin-top: 4mm; padding-top: 3mm; border-top: 1px solid #000; }
              .rc-corte { text-align: center; margin-top: 5mm; padding-top: 3mm; border-top: 2px dashed #000; font-size: 10px !important; }
            }
          </style>
        </head>
        <body>
          <div class="rc">
            <div class="rc-header">
              <div class="rc-logo">${tiendaNombre}</div>
              <div class="rc-sub">DE TODO EN COMPUTACION</div>
              <div class="rc-sub">${tiendaDireccion}</div>
              <div class="rc-sub">Tel: ${tiendaTelefono}</div>
            </div>
            <div class="rc-tipo">PREVENTA</div>
            <div class="rc-num">N° ${preventa.numero_preventa}</div>
            <div style="margin-bottom:3mm;">
              <div class="rc-row"><span class="rc-label">Fecha:</span><span>${formatDateLong(preventa.fecha)}</span></div>
              <div class="rc-row"><span class="rc-label">Cliente:</span><span>${preventa.cliente_nombre}</span></div>
              ${preventa.direccion ? `<div class="rc-row"><span class="rc-label">Direccion:</span><span>${preventa.direccion}</span></div>` : ''}
              <div class="rc-row"><span class="rc-label">NIT:</span><span>${preventa.nit || 'C/F'}</span></div>
            </div>
            <table class="rc-table">
              <thead><tr><th class="cc">CANT</th><th class="cd">DESCRIPCION</th><th class="cp">P/UNIT</th><th class="ct">TOTAL</th></tr></thead>
              <tbody>
                ${items.map(item => `<tr><td class="cc">${parseFloat(item.cantidad).toFixed(0)}</td><td class="cd">${item.descripcion}</td><td class="cp">${formatMoney(item.precio_unitario)}</td><td class="ct">${formatMoney(item.total)}</td></tr>`).join('')}
              </tbody>
            </table>
            <div class="rc-total-s">
              <div class="rc-total"><span>TOTAL</span><span>${formatMoney(preventa.total)}</span></div>
              <div class="rc-total"><span>ANTICIPO</span><span>${formatMoney(preventa.anticipo)}</span></div>
              <div class="rc-saldo"><span>SALDO</span><span>${formatMoney(preventa.saldo)}</span></div>
            </div>
            <div class="rc-firmas">
              <div class="rc-firma"><div class="rc-firma-l"></div><div class="rc-firma-t">${preventa.nombre_creador || user?.nombre || ''}</div></div>
              <div class="rc-firma"><div class="rc-firma-l"></div><div class="rc-firma-t">FIRMA</div></div>
            </div>
            <div class="rc-footer"><p>Gracias por su preferencia</p></div>
            <div class="rc-corte">- - - - - - - - - - - - - - - - - - -</div>
          </div>
          <script>window.addEventListener('load', function() { setTimeout(function() { window.print(); }, 500); });</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleEliminar = async () => {
    if (!window.confirm(`¿Eliminar la preventa N° ${preventa.numero_preventa}?`)) return;
    try {
      await preventasApi.delete(preventa.id);
      navigate('/preventas');
    } catch (err) {
      alert('Error al eliminar: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (!preventa) {
    return <div className="empty-state"><h3>Preventa no encontrada</h3></div>;
  }

  const parsearDesglose = (json) => {
    if (!json) return [];
    try {
      return JSON.parse(json);
    } catch (e) {
      return [];
    }
  };
  const desgloseAnticipo = preventa.metodo_pago_anticipo === 'Mixto' ? parsearDesglose(preventa.detalle_anticipo_mixto) : [];
  const desglosePagoFinal = preventa.metodo_pago_final === 'Mixto' ? parsearDesglose(preventa.detalle_pago_final_mixto) : [];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Preventa N° {preventa.numero_preventa}</h1>
          <p className="page-subtitle">{formatDateLong(preventa.fecha)} - {preventa.cliente_nombre}</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => navigate('/preventas')} className="btn btn-secondary">Volver</button>
          <button onClick={handlePrint} className="btn btn-secondary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Imprimir
          </button>
          {preventa.estado === 'pendiente' && (
            <button onClick={abrirModalPago} className="btn btn-primary">Completar Pago</button>
          )}
          <button onClick={handleEliminar} className="btn btn-danger">Eliminar</button>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>N° Preventa</div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>{preventa.numero_preventa}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Estado</div>
            <span className={`status-badge ${preventa.estado === 'pagada' ? 'status-terminada' : 'status-pendiente'}`}>
              {preventa.estado === 'pagada' ? 'Pagada' : 'Pendiente'}
            </span>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Fecha Preventa</div>
            <div style={{ fontWeight: 500 }}>{formatDateLong(preventa.fecha)}</div>
          </div>
          {parseFloat(preventa.anticipo) > 0 && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Fecha Abono</div>
              <div style={{ fontWeight: 500 }}>{formatDate(preventa.created_at)}</div>
            </div>
          )}
          {preventa.estado === 'pagada' && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Fecha Cancelación</div>
              <div style={{ fontWeight: 500 }}>{formatDate(preventa.fecha_pago_final)}</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Cliente</div>
            <div style={{ fontWeight: 600 }}>{preventa.cliente_nombre}</div>
          </div>
          {preventa.cliente_telefono && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Teléfono</div>
              <div>{preventa.cliente_telefono}</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>NIT</div>
            <div>{preventa.nit || 'C/F'}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Método Anticipo</div>
            <div>{preventa.metodo_pago_anticipo || 'Efectivo'}</div>
            {desgloseAnticipo.length > 0 && (
              <div style={{ marginTop: '6px' }}>
                {desgloseAnticipo.map((p, i) => (
                  <div key={i} style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    {p.metodo}: <strong>{formatMoney(p.monto)}</strong>
                    {p.metodo === 'Cheque' && p.comprobante ? ` (${p.comprobante})` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
          {preventa.estado === 'pagada' && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Método Pago Final</div>
              <div>{preventa.metodo_pago_final}</div>
              {desglosePagoFinal.length > 0 && (
                <div style={{ marginTop: '6px' }}>
                  {desglosePagoFinal.map((p, i) => (
                    <div key={i} style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      {p.metodo}: <strong>{formatMoney(p.monto)}</strong>
                      {p.metodo === 'Cheque' && p.comprobante ? ` (${p.comprobante})` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Creado por</div>
            <div>{preventa.nombre_creador || '-'}</div>
          </div>
        </div>

        <h3 style={{ fontSize: '14px', color: 'var(--text-light)', textTransform: 'uppercase', marginBottom: '12px' }}>
          Detalle
        </h3>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>Cantidad</th>
                <th>Descripción</th>
                <th style={{ textAlign: 'right', width: '120px' }}>P. Unitario</th>
                <th style={{ textAlign: 'right', width: '120px' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {(preventa.items || []).map((item, i) => (
                <tr key={i}>
                  <td style={{ textAlign: 'center' }}>{parseFloat(item.cantidad).toFixed(0)}</td>
                  <td>{item.descripcion}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(item.precio_unitario)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span>Total</span>
            <span style={{ fontWeight: 600 }}>{formatMoney(preventa.total)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span>Anticipo</span>
            <span style={{ fontWeight: 600, color: 'var(--success-color)' }}>{formatMoney(preventa.anticipo)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '18px', fontWeight: 600 }}>Saldo Pendiente</span>
            <span style={{ fontSize: '28px', fontWeight: 700, color: parseFloat(preventa.saldo) > 0 ? 'var(--danger-color)' : 'var(--success-color)' }}>
              {formatMoney(preventa.saldo)}
            </span>
          </div>
        </div>

        <div style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-light)', textAlign: 'right' }}>
          Creado: {formatDateTime(preventa.created_at)}
        </div>
      </div>

      {/* Modal Completar Pago */}
      {showPagoModal && (
        <div className="modal-overlay" onClick={() => setShowPagoModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Completar Pago</h2>
              <button className="modal-close" onClick={() => setShowPagoModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Saldo Pendiente</label>
                <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--danger-color)' }}>
                  {formatMoney(preventa.saldo)}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label required">Monto a Pagar (Q)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={preventa.saldo}
                  className="form-control"
                  value={pagoForm.monto_pago}
                  onChange={(e) => setPagoForm({ ...pagoForm, monto_pago: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Método de Pago</label>
                <select
                  className="form-control"
                  value={pagoForm.metodo_pago}
                  onChange={(e) => setPagoForm({ ...pagoForm, metodo_pago: e.target.value, cuenta_bancaria_id: '', pos_id: '' })}
                >
                  <option value="Efectivo">Efectivo</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="POS">POS</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Mixto">Pago Mixto</option>
                </select>
              </div>
              {pagoForm.metodo_pago === 'Transferencia' && (
                <div className="form-group">
                  <label className="form-label required">Cuenta Bancaria</label>
                  <select className="form-control" value={pagoForm.cuenta_bancaria_id}
                    onChange={(e) => setPagoForm({ ...pagoForm, cuenta_bancaria_id: e.target.value })}>
                    <option value="">-- Seleccionar --</option>
                    {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre} - {c.banco}</option>)}
                  </select>
                </div>
              )}
              {pagoForm.metodo_pago === 'POS' && (
                <div className="form-group">
                  <label className="form-label required">Terminal POS</label>
                  <select className="form-control" value={pagoForm.pos_id}
                    onChange={(e) => setPagoForm({ ...pagoForm, pos_id: e.target.value })}>
                    <option value="">-- Seleccionar --</option>
                    {posTerminales.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
              )}
              {['Transferencia', 'Cheque'].includes(pagoForm.metodo_pago) && (
                <div className="form-group">
                  <label className="form-label required">Número de Referencia</label>
                  <input
                    type="text"
                    className="form-control"
                    value={pagoForm.referencia_pago}
                    onChange={(e) => setPagoForm({ ...pagoForm, referencia_pago: e.target.value })}
                  />
                </div>
              )}
              {pagoForm.metodo_pago === 'Mixto' && (
                <div className="form-group">
                  <label className="form-label">Desglose del Pago</label>
                  <PagoMixtoForm
                    pagos={pagoForm.pago_mixto}
                    onChange={(pagos) => setPagoForm(prev => ({ ...prev, pago_mixto: pagos }))}
                    total={parseFloat(pagoForm.monto_pago) || 0}
                    cuentas={cuentas}
                    posTerminales={posTerminales}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPagoModal(false)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleCompletarPago} disabled={pagando}>
                {pagando ? 'Guardando...' : 'Registrar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreventaDetalle;
